import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const PHONE_RE    = /^\+[1-9]\d{7,14}$/;
const PINCODE_RE  = /^\d{6}$/;
const INDIA_PHONE = /^[6-9]\d{9}$/;

export default function PatientProfile() {
  const navigate = useNavigate();

  const [loading,     setLoading]     = useState(true);
  const [submitting,  setSubmitting]  = useState(false);
  const [toast,       setToast]       = useState(null); // { type: 'success'|'error', msg }
  const [isTempMobile, setIsTempMobile] = useState(false);

  const [form, setForm] = useState({
    phone:   '',
    pincode: '',
    city:    '',
    state:   '',
  });

  // Track original values so we only send changed fields
  const [original, setOriginal] = useState({ phone: '', pincode: '', city: '', state: '' });

  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  }, []);

  useEffect(function fetchProfile() {
    (async () => {
      try {
        const res = await api.get('/auth/patient/me/profile');
        const d   = res.data.data;
        const vals = {
          phone:   d.phone   || '',
          pincode: d.location?.pincode || '',
          city:    d.location?.city    || '',
          state:   d.location?.state   || '',
        };
        setForm(vals);
        setOriginal(vals);
        setIsTempMobile(d.isTempMobile || false);
      } catch {
        showToast('error', 'Could not load profile. Please refresh.');
      } finally {
        setLoading(false);
      }
    })();
  }, [showToast]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  }

  // Auto-format: 10-digit Indian mobile (starts 6–9) → prepend +91
  function handlePhoneBlur() {
    const v = form.phone.trim();
    if (INDIA_PHONE.test(v)) {
      setForm(f => ({ ...f, phone: '+91' + v }));
    }
  }

  // On pincode blur: if 6 digits, call India Post API to auto-fill city + state
  async function handlePincodeBlur() {
    const pin = form.pincode.trim();
    if (!PINCODE_RE.test(pin)) return;
    try {
      const res  = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const data = await res.json();
      if (data[0]?.Status === 'Success' && data[0].PostOffice?.length > 0) {
        const po = data[0].PostOffice[0];
        setForm(f => ({
          ...f,
          city:  f.city  || po.District || '',
          state: f.state || po.State    || '',
        }));
      }
    } catch {
      // Non-fatal — user can type manually
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;

    // Client-side validation
    if (form.phone && !PHONE_RE.test(form.phone)) {
      showToast('error', 'Please enter a valid phone number (+91XXXXXXXXXX)');
      return;
    }
    if (form.pincode && !PINCODE_RE.test(form.pincode)) {
      showToast('error', 'Please enter a valid 6-digit pincode');
      return;
    }

    // Only send changed fields
    const payload = {};
    if (form.phone   !== original.phone)   payload.phone   = form.phone;
    if (form.pincode !== original.pincode) payload.pincode = form.pincode;
    if (form.city    !== original.city)    payload.city    = form.city;
    if (form.state   !== original.state)   payload.state   = form.state;

    if (Object.keys(payload).length === 0) {
      showToast('success', 'No changes to save');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.patch('/auth/patient/me/profile', payload);
      const d   = res.data.data;
      const updated = {
        phone:   d.phone              || '',
        pincode: d.location?.pincode  || '',
        city:    d.location?.city     || '',
        state:   d.location?.state    || '',
      };
      setForm(updated);
      setOriginal(updated);
      if (payload.phone) setIsTempMobile(false);
      showToast('success', 'Profile updated');
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        showToast('error', 'This number is already in use');
      } else if (status === 400) {
        const msg = err.response?.data?.message || '';
        if (msg.toLowerCase().includes('phone')) {
          showToast('error', 'Please enter a valid phone number (+91XXXXXXXXXX)');
        } else if (msg.toLowerCase().includes('pincode')) {
          showToast('error', 'Please enter a valid 6-digit pincode');
        } else {
          showToast('error', msg || 'Validation error');
        }
      } else {
        showToast('error', 'Could not save profile. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <div style={S.page}><div style={S.loading}>Loading profile…</div></div>;
  }

  return (
    <div style={S.page}>
      {/* Toast */}
      {toast && (
        <div style={{ ...S.toast, background: toast.type === 'success' ? '#2ABFAB' : '#e74c3c' }}>
          {toast.msg}
        </div>
      )}

      <div style={S.card}>
        <div style={S.header}>
          <button onClick={() => navigate(-1)} style={S.backBtn}>← Back</button>
          <h2 style={S.title}>My Profile</h2>
        </div>

        <form onSubmit={handleSubmit}>
          {/* ── Phone section ── */}
          <div style={S.section}>
            <h3 style={S.sectionTitle}>Phone Number</h3>

            {isTempMobile && (
              <div style={S.warning}>
                Your healer cannot reach you without a phone number. Please update it below.
              </div>
            )}

            <label style={S.label}>Phone number</label>
            <input
              name="phone"
              type="tel"
              value={form.phone}
              onChange={handleChange}
              onBlur={handlePhoneBlur}
              placeholder="+919876543210"
              style={S.input}
            />
            <p style={S.hint}>Indian numbers: enter 10 digits and we'll add +91 automatically.</p>
          </div>

          {/* ── Address section ── */}
          <div style={S.section}>
            <h3 style={S.sectionTitle}>Address</h3>

            <label style={S.label}>Pincode</label>
            <input
              name="pincode"
              type="text"
              value={form.pincode}
              onChange={handleChange}
              onBlur={handlePincodeBlur}
              placeholder="600001"
              maxLength={6}
              style={S.input}
            />
            <p style={S.hint}>Enter pincode and city/state will auto-fill.</p>

            <label style={S.label}>City</label>
            <input
              name="city"
              type="text"
              value={form.city}
              onChange={handleChange}
              placeholder="Chennai"
              style={S.input}
            />

            <label style={S.label}>State</label>
            <input
              name="state"
              type="text"
              value={form.state}
              onChange={handleChange}
              placeholder="Tamil Nadu"
              style={S.input}
            />
          </div>

          <button type="submit" disabled={submitting} style={S.submitBtn}>
            {submitting ? 'Saving…' : 'Save Profile'}
          </button>
        </form>
      </div>
    </div>
  );
}

const S = {
  page:        { minHeight: '100vh', background: '#f5f7f5', padding: '24px 16px', fontFamily: "'Raleway', sans-serif" },
  loading:     { padding: '40px', textAlign: 'center', color: '#666', fontSize: '14px' },
  toast:       { position: 'fixed', top: '16px', left: '50%', transform: 'translateX(-50%)', padding: '12px 24px', borderRadius: '8px', color: '#fff', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', zIndex: 9999, minWidth: '200px', textAlign: 'center' },
  card:        { maxWidth: '480px', margin: '0 auto', background: '#fff', borderRadius: '12px', padding: '24px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
  header:      { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' },
  backBtn:     { background: 'none', border: 'none', color: '#2ABFAB', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '700', cursor: 'pointer', padding: '0' },
  title:       { margin: '0', fontFamily: "'Raleway', sans-serif", fontSize: '20px', fontWeight: '800', color: '#2d2d2d' },
  section:     { marginBottom: '28px' },
  sectionTitle: { margin: '0 0 16px', fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '800', color: '#2ABFAB', textTransform: 'uppercase', letterSpacing: '1px' },
  warning:     { background: 'rgba(255,160,60,0.1)', border: '1px solid rgba(255,160,60,0.4)', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px', color: '#b86f10', fontFamily: "'Raleway', sans-serif", fontSize: '13px', fontWeight: '600', lineHeight: '1.5' },
  label:       { display: 'block', fontFamily: "'Raleway', sans-serif", fontSize: '12px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' },
  input:       { display: 'block', width: '100%', padding: '12px 14px', border: '1px solid #e0e0e0', borderRadius: '8px', fontFamily: "'Raleway', sans-serif", fontSize: '14px', color: '#2d2d2d', background: '#fafafa', boxSizing: 'border-box', marginBottom: '4px' },
  hint:        { margin: '0 0 16px', fontFamily: "'Raleway', sans-serif", fontSize: '11px', color: '#999' },
  submitBtn:   { width: '100%', padding: '14px', background: '#2ABFAB', border: 'none', borderRadius: '8px', fontFamily: "'Raleway', sans-serif", fontSize: '14px', fontWeight: '800', color: '#fff', cursor: 'pointer', letterSpacing: '0.5px' },
};
